import {observer} from 'mobx-react-lite';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {FiChevronDown} from 'react-icons/fi';
import {useDebouncedCallback} from 'use-debounce';
import {useNavigate} from 'react-router-dom';
import {useActionItems} from '@hooks/features-action-items';
import {useFirebaseFetch} from '@hooks/query-hook';
import {useStoreContext} from '@store/store-context';
import {Avatar} from '@ui/atoms/avatar';
import {ItemLoader} from '@ui/organisms/item-loader';
import {SearchBox2} from '@ui/molecules/search-box';
import {UsersGoalsLayout} from '@ui/layouts/users-goals';
import ObjectiveFilter from '../objective-filter';
import {
  activeFilterCount,
  filterMyGoals,
  GoalEngagementSummary,
  goalIsOverdueCarryover,
  goalStatusSuggestion,
  sortMyGoals,
  summarizeGoalEngagement,
  summarizeMyGoals,
} from '../my-objective/my-goals-v2.utils';
import {MyGoalCard} from '../my-objective/my-goals-v2';
import {
  emptyMyGoalsFilters,
  MyGoalsFilters,
  MyGoalsLifecycleFilter,
  MyGoalsSort,
} from '../my-objective/my-goals-v2.types';
import {useDirectReportHook} from './direct-report-hook';
import {TeamGoalsOverview} from './team-goals-overview';
import {TeamGoalsToolbar} from './team-goals-toolbar';
import {TeamGoalMember, TeamGoalsGroupBy} from './team-goals.types';
import {
  buildTeamGoalMembers,
  groupTeamGoalsByStatus,
  summarizeTeamGoals,
  sortTeamGoalMembers,
  teamMemberName,
  teamHealthBarItems,
  teamPerformanceItems,
  teamStatusLabels,
  teamStatusOrder,
} from './team-goals.utils';
import {
  AttentionSummary,
  EmptyPanel,
  FilterWrapper,
  LoadingPanel,
  NoGoalsActions,
  NoGoalsPill,
  PeopleList,
  PersonCards,
  PersonChevron,
  PersonHeader,
  PersonIdentity,
  PersonIdentityButton,
  PersonName,
  PersonOutcome,
  PersonSection,
  PersonSummary,
  SegmentedTrack,
  StackedList,
  StatusHeading,
  StatusCards,
  StatusList,
  StatusSection,
  StatusSegment,
  Wrapper,
} from './styles';

const idOf = (value: any) => value?.id || value?._id || '';

// The flat view reuses the same cards across person and status groupings.

const TeamCard = ({
  goal,
  users,
  engagement,
  expanded,
  onToggle,
}: {
  goal: any;
  users: any[];
  engagement: GoalEngagementSummary;
  expanded: boolean;
  onToggle: () => void;
}) => (
  <MyGoalCard
    goal={goal}
    availableUsers={users}
    engagement={engagement}
    expanded={expanded}
    onToggle={onToggle}
    managerEdit
    showCreatedAt={!goal?.isKpi}
    footerPrompt="Want to comment or create action items?"
  />
);

export const GoalsDirectReports = observer(
  ({scope = 'team'}: {scope?: 'team' | 'all'}) => {
    const {
      loading,
      error,
      filters: requestFilters,
      view,
      userFilter,
      setUserFilter,
      setSearchValue,
      dateRange,
      clearSelection,
      setClearSelection,
      setDateRange,
      refetch,
    } = useDirectReportHook(scope);
    const {
      authStore: {auth},
      companyStore: {directReports},
      groupStore: {allGroups},
      goalsState,
      usersStore: {users},
    } = useStoreContext();
    const navigate = useNavigate();
    const firebaseGoals = useFirebaseFetch('goals');
    const goalMentions = useFirebaseFetch(
      `users/${auth.user.id}/mentions/goals`,
    );
    const goalActionItems = useActionItems({
      source: 'goal',
      itemId: '',
      userId: '',
      fetchAll: true,
    });
    const [groupBy, setGroupBy] = useState<TeamGoalsGroupBy>('person');
    const [teamFilters, setTeamFilters] = useState<MyGoalsFilters>(
      emptyMyGoalsFilters,
    );
    const [lifecycle, setLifecycle] = useState<MyGoalsLifecycleFilter>('all');
    const [sort, setSort] = useState<MyGoalsSort>('highest_progress');
    const [searchInput, setSearchInput] = useState('');
    const [selectedPeople, setSelectedPeople] = useState<string[]>([]);
    const [reviewSuggestedOnly, setReviewSuggestedOnly] = useState(false);
    const [defaultPeriod] = useState(dateRange);
    const [expandedPeople, setExpandedPeople] = useState<Set<string>>(
      new Set(),
    );
    const [expandedGoals, setExpandedGoals] = useState<Set<string>>(new Set());

    const debounceValue = useDebouncedCallback(
      (value: string) => setSearchValue(value),
      600,
    );
    const selectedMemberIds = useMemo(
      () =>
        String(requestFilters?.member || '')
          .split(',')
          .filter(Boolean),
      [requestFilters?.member],
    );
    const members = useMemo(
      () =>
        buildTeamGoalMembers({
          reports: directReports,
          users,
          managerId: requestFilters?.manager,
          memberIds: selectedMemberIds,
          searching: Boolean(requestFilters?.name),
        }),
      [
        directReports,
        requestFilters?.manager,
        requestFilters?.name,
        selectedMemberIds,
        users,
      ],
    );
    const allGoals = useMemo(
      () => members.flatMap((member) => member.goals || []),
      [members],
    );
    const periodGoals = useMemo(
      () =>
        filterMyGoals({
          goals: allGoals,
          period: dateRange,
          filters: emptyMyGoalsFilters,
          lifecycle: 'all',
        }),
      [allGoals, dateRange],
    );
    const displayGoals = useMemo(
      () =>
        filterMyGoals({
          goals: allGoals,
          period: dateRange,
          filters: emptyMyGoalsFilters,
          lifecycle: 'all',
          includeOverdueCarryover: true,
        }),
      [allGoals, dateRange],
    );
    const periodReportMembers = useMemo(
      () =>
        (directReports || []).map((member: TeamGoalMember) => ({
          ...member,
          goals: filterMyGoals({
            goals: member.goals || [],
            period: dateRange,
            filters: emptyMyGoalsFilters,
            lifecycle: 'all',
            includeOverdueCarryover: true,
          }),
        })),
      [dateRange, directReports],
    );
    const carryoverCount = useMemo(
      () =>
        displayGoals.filter((goal) => goalIsOverdueCarryover(goal, dateRange))
          .length,
      [dateRange, displayGoals],
    );
    const summary = useMemo(() => summarizeMyGoals(periodGoals), [periodGoals]);
    const statusSuggestionsByGoal = useMemo(
      () =>
        periodGoals.reduce((suggestions, goal) => {
          if (goalStatusSuggestion(goal)) suggestions[goal.id] = true;
          return suggestions;
        }, {} as Record<string, boolean>),
      [periodGoals],
    );
    const statusReviewCount = Object.keys(statusSuggestionsByGoal).length;
    const visibleMembers = useMemo(() => {
      const hasGoalCriteria =
        activeFilterCount(teamFilters) > 0 ||
        lifecycle !== 'all' ||
        reviewSuggestedOnly;
      const filteredMembers = members
        .filter(
          (member) =>
            selectedPeople.length === 0 ||
            selectedPeople.includes(idOf(member.assignee)),
        )
        .map((member) => {
          const matchingGoals = filterMyGoals({
            goals: member.goals || [],
            period: dateRange,
            filters: teamFilters,
            lifecycle,
            includeOverdueCarryover: true,
          }).filter(
            (goal) => !reviewSuggestedOnly || statusSuggestionsByGoal[goal.id],
          );
          return {...member, goals: sortMyGoals(matchingGoals, sort)};
        })
        .filter((member) => !hasGoalCriteria || member.goals.length > 0);
      return sortTeamGoalMembers(filteredMembers, sort);
    }, [
      dateRange,
      lifecycle,
      members,
      reviewSuggestedOnly,
      selectedPeople,
      sort,
      statusSuggestionsByGoal,
      teamFilters,
    ]);
    const visibleGoalCount = useMemo(
      () =>
        visibleMembers.reduce(
          (count, member) => count + member.goals.length,
          0,
        ),
      [visibleMembers],
    );
    const statusSections = useMemo(
      () => groupTeamGoalsByStatus(visibleMembers, sort),
      [sort, visibleMembers],
    );
    const engagementByGoal = useMemo(
      () =>
        displayGoals.reduce((summaries, goal) => {
          summaries[goal.id] = summarizeGoalEngagement({
            goal,
            firebaseGoal: firebaseGoals.data?.[goal.id],
            mentions: goalMentions.data?.[goal.id],
            actionItems: goalActionItems.tasks,
            commentsLoading: firebaseGoals.isLoading,
            actionItemsLoading: goalActionItems.isLoading,
          });
          return summaries;
        }, {} as Record<string, GoalEngagementSummary>),
      [
        displayGoals,
        firebaseGoals.data,
        firebaseGoals.isLoading,
        goalActionItems.isLoading,
        goalActionItems.tasks,
        goalMentions.data,
      ],
    );
    const memberSignature = visibleMembers
      .map((member) => `${idOf(member.assignee)}:${member.goals.length}`)
      .join('|');

    useEffect(() => {
      setExpandedPeople((current) => {
        const validIds = new Set(
          visibleMembers.map((member) => idOf(member.assignee)),
        );
        const retained = new Set(
          Array.from(current).filter((id) => validIds.has(id)),
        );
        if (retained.size) return retained;
        const first = visibleMembers.find((member) => member.goals.length > 0);
        return first ? new Set([idOf(first.assignee)]) : retained;
      });
    }, [memberSignature, visibleMembers]);

    useEffect(() => {
      if (reviewSuggestedOnly && statusReviewCount === 0) {
        setReviewSuggestedOnly(false);
      }
    }, [reviewSuggestedOnly, statusReviewCount]);

    const toggleGoal = useCallback((goalId: string) => {
      setExpandedGoals((current) => {
        const next = new Set(current);
        next.has(goalId) ? next.delete(goalId) : next.add(goalId);
        return next;
      });
    }, []);

    const createGoalFor = (member: any) => {
      goalsState.setShowGoalForm(true);
      goalsState.setEditing(false);
      navigate('/create-goal', {state: {assigneeId: idOf(member)}});
    };

    const renderCard = (goal: any) => (
      <TeamCard
        key={goal.id}
        goal={goal}
        users={users}
        engagement={engagementByGoal[goal.id]}
        expanded={expandedGoals.has(goal.id)}
        onToggle={() => toggleGoal(goal.id)}
      />
    );

    return (
      <Wrapper $compact={scope === 'team' && view === 'flat'}>
        {(scope === 'all' || view === 'stacked') && (
          <FilterWrapper>
            <div className="pd">
              <ObjectiveFilter
                userFilter={userFilter}
                setUserFilter={setUserFilter}
                clearSelection={clearSelection}
                filterOnlyTeams={scope === 'team'}
                directReportFilter={scope === 'team'}
                showDateRange
                setClearSelection={setClearSelection}
                setDateRange={setDateRange}
                dateRange={dateRange}
              />
            </div>
            <div className="s-b-wrapper">
              <SearchBox2
                placeholder="Search goals"
                inputStyle={{height: '56px', borderRadius: '10px'}}
                onChange={(event) => debounceValue(event.target.value)}
                handleCancel={() => setSearchValue('')}
              />
            </div>
          </FilterWrapper>
        )}

        {!loading && !error && scope === 'team' && view === 'flat' && (
          <>
            <TeamGoalsOverview
              summary={summary}
              carryoverCount={carryoverCount}
              lifecycle={lifecycle}
              statusReviewCount={statusReviewCount}
              reviewSuggestedOnly={reviewSuggestedOnly}
              onLifecycleChange={(value) => {
                setLifecycle(value);
                setReviewSuggestedOnly(false);
              }}
              onReview={() => {
                setTeamFilters(emptyMyGoalsFilters);
                setSelectedPeople([]);
                setLifecycle('active');
                setExpandedPeople(new Set());
                setReviewSuggestedOnly(true);
              }}
            />
            <TeamGoalsToolbar
              goals={displayGoals}
              filters={teamFilters}
              sort={sort}
              groupBy={groupBy}
              period={dateRange}
              defaultPeriod={defaultPeriod}
              memberOptions={members.map((member) => ({
                id: idOf(member.assignee),
                name: teamMemberName(member.assignee),
              }))}
              selectedPeople={selectedPeople}
              reviewSuggestedOnly={reviewSuggestedOnly}
              onClearStatusReview={() => setReviewSuggestedOnly(false)}
              searchValue={searchInput}
              searchPlaceholder="Search goals"
              resultCount={visibleGoalCount}
              onSearchChange={(value) => {
                setSearchInput(value);
                debounceValue(value);
              }}
              onFiltersChange={(value) => {
                setTeamFilters(value);
                setReviewSuggestedOnly(false);
              }}
              onSortChange={setSort}
              onGroupByChange={setGroupBy}
              onPeriodChange={setDateRange}
              onSelectedPeopleChange={(value) => {
                setSelectedPeople(value);
                setReviewSuggestedOnly(false);
              }}
            />
          </>
        )}

        {loading && (
          <LoadingPanel aria-label="Loading Team Goals">
            <ItemLoader />
          </LoadingPanel>
        )}

        {!loading && error && (
          <EmptyPanel role="alert">
            <div>
              <h2>Team Goals could not be loaded</h2>
              <p>Check your connection and try again.</p>
              <button type="button" onClick={() => refetch()}>
                Try again
              </button>
            </div>
          </EmptyPanel>
        )}

        {!loading &&
          !error &&
          (scope === 'team'
            ? members.length === 0
            : periodReportMembers.length === 0) && (
            <EmptyPanel>
              <div>
                <h2>No Team Goals found</h2>
                <p>Try changing the selected people, dates, or search.</p>
              </div>
            </EmptyPanel>
          )}

        {!loading &&
          !error &&
          scope === 'team' &&
          view === 'flat' &&
          members.length > 0 &&
          ((groupBy === 'person' && visibleMembers.length === 0) ||
            (groupBy === 'status' && visibleGoalCount === 0)) && (
            <EmptyPanel>
              <div>
                <h2>No Team Goals match these filters</h2>
                <p>Try clearing a filter or selecting another period.</p>
              </div>
            </EmptyPanel>
          )}

        {!loading &&
          !error &&
          (scope === 'all' || view === 'stacked') &&
          periodReportMembers.length > 0 && (
            <StackedList>
              {periodReportMembers.map((member: TeamGoalMember) => (
                <UsersGoalsLayout
                  key={idOf(member.assignee)}
                  userId={idOf(member.assignee)}
                  avatar={member?.assignee?.avatar?.url}
                  name={teamMemberName(member.assignee)}
                  jobDescription={member.assignee?.jobTitle}
                  goals={member.goals}
                  variant="companyTeams"
                  tooltip
                  isFetching={loading}
                  view={view}
                  allGroups={allGroups}
                />
              ))}
            </StackedList>
          )}

        {!loading &&
          !error &&
          scope === 'team' &&
          visibleMembers.length > 0 &&
          view === 'flat' &&
          groupBy === 'person' && (
            <PeopleList>
              {visibleMembers.map((member) => {
                const memberId = idOf(member.assignee);
                const summary = summarizeTeamGoals(member.goals);
                const expanded = expandedPeople.has(memberId);
                const firstName =
                  member.assignee?.firstName || teamMemberName(member.assignee);
                const personIdentity = (
                  <>
                    <Avatar
                      src={member.assignee?.avatar?.url}
                      name={teamMemberName(member.assignee)}
                      userId={memberId}
                      tooltip
                      size="lg"
                    />
                    <PersonName>
                      <h2>{teamMemberName(member.assignee)}</h2>
                      <p>
                        {member.assignee?.jobTitle || 'Team member'} ·{' '}
                        {summary.total} goal
                        {summary.total === 1 ? '' : 's'}
                      </p>
                    </PersonName>
                  </>
                );
                return (
                  <PersonSection key={memberId}>
                    <PersonHeader>
                      {summary.total > 0 ? (
                        <PersonIdentityButton
                          type="button"
                          aria-expanded={expanded}
                          aria-label={`${expanded ? 'Collapse' : 'Expand'} ${
                            summary.total
                          } goals for ${teamMemberName(member.assignee)}`}
                          onClick={() =>
                            setExpandedPeople((current) => {
                              const next = new Set(current);
                              next.has(memberId)
                                ? next.delete(memberId)
                                : next.add(memberId);
                              return next;
                            })
                          }>
                          {personIdentity}
                          <PersonChevron
                            $expanded={expanded}
                            aria-hidden="true">
                            <FiChevronDown size={14} />
                          </PersonChevron>
                        </PersonIdentityButton>
                      ) : (
                        <PersonIdentity>{personIdentity}</PersonIdentity>
                      )}

                      {summary.total > 0 ? (
                        <PersonSummary className="person-summary">
                          <SegmentedTrack aria-label="Goals by health">
                            {teamHealthBarItems(summary).map(
                              ({performance, count, width}) => (
                                <StatusSegment
                                  key={performance}
                                  $performance={performance}
                                  $width={width}
                                  title={`${count} ${teamStatusLabels[performance]}`}
                                />
                              ),
                            )}
                          </SegmentedTrack>
                          <AttentionSummary>
                            {teamPerformanceItems(summary).map(
                              ({performance, count, label}, index) => (
                                <span key={performance}>
                                  {index > 0 && <i aria-hidden="true">·</i>}
                                  <strong>{count}</strong> {label}
                                </span>
                              ),
                            )}
                          </AttentionSummary>
                        </PersonSummary>
                      ) : null}

                      {summary.total > 0 ? (
                        <PersonOutcome>
                          <span>Avg. progress</span>
                          <strong>{summary.averageProgress}%</strong>
                        </PersonOutcome>
                      ) : (
                        <NoGoalsActions>
                          <NoGoalsPill>No goals set</NoGoalsPill>
                          <button
                            type="button"
                            aria-label={`Create goals for ${firstName}`}
                            onClick={() => createGoalFor(member.assignee)}>
                            Create goals
                          </button>
                        </NoGoalsActions>
                      )}
                    </PersonHeader>

                    {expanded && summary.total > 0 && (
                      <PersonCards>{member.goals.map(renderCard)}</PersonCards>
                    )}
                  </PersonSection>
                );
              })}
            </PeopleList>
          )}

        {!loading &&
          !error &&
          scope === 'team' &&
          statusSections.length > 0 &&
          view === 'flat' &&
          groupBy === 'status' && (
            <StatusList>
              {statusSections.map((section) => (
                <StatusSection key={section.performance}>
                  <StatusHeading $performance={section.performance}>
                    {section.goals.length}{' '}
                    {teamStatusLabels[section.performance]}
                  </StatusHeading>
                  <StatusCards>{section.goals.map(renderCard)}</StatusCards>
                </StatusSection>
              ))}
            </StatusList>
          )}
      </Wrapper>
    );
  },
);
